import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAdminDashboard, GetAdminDashboardOutputType } from 'zite-endpoints-sdk';
import { Skeleton } from '@/components/ui/skeleton';
import { format, parseISO } from 'date-fns';
import { Users, AlertCircle, Star, Package, MessageSquare, Calendar, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';

type DashData = GetAdminDashboardOutputType;

function KpiTile({ label, value, sub, color, onClick }: { label: string; value: number; sub?: string; color?: string; onClick?: () => void }) {
  return (
    <div
      className={`bg-card border border-border rounded-lg p-4 ${onClick ? 'cursor-pointer hover:border-primary/50 transition-colors' : ''}`}
      onClick={onClick}
    >
      <p className="text-xs text-muted-foreground uppercase tracking-wider">{label}</p>
      <p className={`text-2xl font-bold mt-1 ${color || 'text-foreground'}`}>{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
    </div>
  );
}

export default function AdminDashboardTab({ onNavigate }: { onNavigate: (section: string) => void }) {
  const [data, setData] = useState<DashData | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    getAdminDashboard({}).then(setData).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24" />)}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Skeleton className="h-40" /><Skeleton className="h-40" />
      </div>
    </div>
  );

  if (!data) return null;

  const formatDate = (d: string) => {
    try { return format(parseISO(d), 'dd MMM'); } catch { return d; }
  };

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiTile
          label="Active Employees"
          value={data.activeEmployees}
          sub={data.onboardingCount > 0 ? `${data.onboardingCount} pending onboarding` : 'All active'}
          onClick={() => onNavigate('employees')}
        />
        <KpiTile
          label="Pending Authorisations"
          value={data.pendingTraining + data.pendingEdits}
          sub={`${data.pendingTraining} training · ${data.pendingEdits} edit`}
          color={data.pendingTraining + data.pendingEdits > 0 ? 'text-primary' : undefined}
          onClick={() => onNavigate('training-reqs')}
        />
        <KpiTile
          label="Outstanding Reviews"
          value={data.pendingReviews}
          sub="Performance reviews"
          onClick={() => onNavigate('reviews')}
        />
        <KpiTile
          label="Open Requests"
          value={data.uniformRequests + data.keyRequests}
          sub={`${data.uniformRequests} uniform · ${data.keyRequests} key`}
          onClick={() => onNavigate('uniforms')}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {/* Messages & Announcements */}
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-semibold text-foreground flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-primary" /> Messages & Announcements
            </p>
            {data.unreadReplies > 0 && (
              <span className="text-xs text-primary font-medium">{data.unreadReplies} unread</span>
            )}
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Unread message replies</span>
              <span className="font-medium text-foreground">{data.unreadReplies}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Active announcements</span>
              <span className="font-medium text-foreground">{data.activeAnnouncements}</span>
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => onNavigate('messaging')}>
              View Messages
            </Button>
            <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => onNavigate('announcements')}>
              Announcements
            </Button>
          </div>
        </div>

        {/* Upcoming Events */}
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-semibold text-foreground flex items-center gap-2">
              <Calendar className="w-4 h-4 text-primary" /> Upcoming Events
            </p>
            <Button size="sm" variant="ghost" className="text-xs h-7 text-primary" onClick={() => onNavigate('events')}>
              <Plus className="w-3 h-3 mr-1" /> Add
            </Button>
          </div>
          {data.upcomingEvents.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">No upcoming events</p>
          ) : (
            <div className="space-y-2">
              {data.upcomingEvents.slice(0, 3).map(e => (
                <div key={e.id} className="flex items-center gap-3">
                  <div className="w-10 text-center bg-muted rounded-md py-1 shrink-0">
                    <p className="text-sm font-bold text-primary leading-none">{e.date ? formatDate(e.date).split(' ')[0] : '?'}</p>
                    <p className="text-xs text-muted-foreground leading-tight">{e.date ? formatDate(e.date).split(' ')[1] : ''}</p>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{e.eventTitle}</p>
                    <p className="text-xs text-muted-foreground">
                      {[e.time, e.location, e.guestCount ? `${e.guestCount} guests` : ''].filter(Boolean).join(' · ')}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
          {data.upcomingEvents.length > 3 && (
            <Button size="sm" variant="ghost" className="text-xs h-7 mt-2 w-full text-muted-foreground" onClick={() => onNavigate('events')}>
              View all {data.upcomingEvents.length} events →
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
